import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useEstimateRefresh } from "@/context/EstimateRefreshContext";
import { useToast } from "@/hooks/use-toast";

type RetryItem = { id: number; propertyId: number | null; taskType: string };

const ESTIMATE_TASKS = ["zillow_search", "rentcast_enrichment"];

export function EstimateRefreshWatcher() {
  const { activeRefreshes, removeRefresh } = useEstimateRefresh();
  const { toast } = useToast();

  // Query both pending and processing so the toast only fires on true completion
  const { data: activeItems = [], dataUpdatedAt } = useQuery<RetryItem[]>({
    queryKey: ["/api/retry-queue", { status: "active" }],
    queryFn: () =>
      Promise.all([
        fetch("/api/retry-queue?status=pending", { credentials: "include" }).then(r => r.json()),
        fetch("/api/retry-queue?status=processing", { credentials: "include" }).then(r => r.json()),
      ]).then(([pending, processing]) => [...pending, ...processing]),
    enabled: activeRefreshes.length > 0,
    refetchInterval: 3000,
  });

  useEffect(() => {
    if (activeRefreshes.length === 0 || dataUpdatedAt === 0) return;

    const activePropertyIds = new Set(
      activeItems
        .filter(i => ESTIMATE_TASKS.includes(i.taskType) && i.propertyId != null)
        .map(i => i.propertyId)
    );

    activeRefreshes.forEach(entry => {
      // Only fire completion once we have data fetched *after* this refresh was triggered
      if (dataUpdatedAt <= entry.addedAt) return;
      if (!activePropertyIds.has(entry.id)) {
        toast({ title: "Estimates updated", description: entry.address ?? undefined });
        removeRefresh(entry.id);
      }
    });
  }, [activeItems, activeRefreshes, dataUpdatedAt]);

  return null;
}
